import {
  defaultBadgeComponent,
  defaultBulletComponent,
  defaultButtonBaseComponent,
  defaultButtonComponent,
  defaultCardComponent,
  defaultChildrenContainer,
  defaultCloseModalComponent,
  defaultContainerComponent,
  defaultContainerOptions,
  defaultDivComponent,
  defaultDividerComponent,
  defaultDividingHeaderComponent,
  defaultDrawerComponent,
  defaultDropdownComponent,
  defaultHeadingComponent,
  defaultHTMLComponent,
  defaultImageBaseComponent,
  defaultImageComponent,
  defaultListComponent,
  defaultListV2Component,
  defaultLottieComponent,
  defaultMarqueeComponent,
  defaultMarqueeItemComponent,
  defaultModalComponent,
  defaultParagraphComponent,
  defaultProductsComponent,
  defaultRatingComponent,
  defaultReviewComponent,
  defaultSectionComponent,
  defaultSubheadingComponent,
  defaultSubtitleComponent,
  defaultTableComponent,
  defaultTextComponent,
  defaultTimelineComponent,
  defaultTimelineItemComponent,
  defaultTitleComponent,
  defaultVideoComponent,
  elementsWithChildren,
} from "app/paywallBuilderV2/components/ElementDetails/utils"
import { TMarqueeItemCount } from "app/paywallBuilderV2/components/Navigator/Element"
import { createComponentId } from "app/paywallBuilderV2/helpers"
import { TElement } from "app/schema/paywallTemplate/element/element"
import { TPageSettings } from "app/schema/paywallTemplate/pageSettings"
import { TPaywallTemplate } from "app/schema/paywallTemplate/paywallTemplate"
import _ from "lodash"

export const defaultPageSettings: TPageSettings = {
  autoAdjustContentAlignment: "true",
  backgroundColor: "rgba(19, 20, 23, 0.7)",
  backgroundContentMaxWidth: "paywallMaxWidth",
  badgeRadius: "8px",
  borderColor: "rgb(70,70,70)",
  borderWidth: "1px",
  brandColor: "#4E71FF",
  brandSecondaryColor: "",
  brandTextColor: "#FFFFFF",
  buttonBackgroundColor: "",
  cardBackgroundBlur: "true",
  cardPadding: "calc(var(--sw-spacing) * 1.6)",
  containerRadius: "25px",
  contentAlignment: "top",
  contentPadding: "calc(var(--sw-spacing) * 1.6)",
  direction: "vertical",
  font: "",
  fontSize: "calc(min(4.5vw, 1rem))",
  footer: "normal",
  footerAlignment: "spread",
  footerDivider: "show",
  footerPosition: "fixed",
  forceDarkMode: "false",
  foregroundColor: "#FFFFFF",
  googleFonts: "",
  headingFont: "",
  hideProducts: "true",
  insetsTopContent: "true",
  linkSelectedProduct: "false",
  logoHeight: "35px",
  logoURL: "../public/assets/logo.svg",
  minNavbarHeight: "64px",
  nav: "show",
  navBackgroundBlur: "true",
  navBackgroundColor: "auto",
  navButtonIconsSize: "42px",
  navCenter: "show",
  navLeft: "mixed",
  navLeftIconURL: "../public/assets/nav-button.svg",
  navLeftInnerIconURL: "../public/assets/nav-button.svg",
  navPosition: "fixed",
  navRight: "mixed",
  navRightIconURL: "../public/assets/nav-button.svg",
  navRightInnerIconURL: "../public/assets/nav-button.svg",
  paywallBackgroundColor: "#292929",
  paywallMaxWidth: "480px",
  pjs: "https://cdn.superwall.me/runtime/entrypoint.js",
  previewDarkMode: "false",
  primaryProductBadge: "show",
  productBackgroundColor: "rgba(0,0,0,0.85)",
  productCount: "one",
  productDescription: "show",
  productDivider: "show",
  productPadding: "15px",
  productRadius: "12px",
  purchaseRadius: "15px",
  purchaseButtonLineTwo: "show",
  reverseDirection: "false",
  secondaryProductBadge: "hide",
  sectionPaddingX: "20px",
  sectionPaddingY: "20px",
  selectedBorderWidth: "2px",
  showProductCheckMark: "hide",
  selectedCheckBoxImage: "https://static.superwallassets.com/uoQsDwmrnamiqkfkzRKvC",
  unselectedCheckBoxImage: "https://static.superwallassets.com/ElRGbHkiRQTvFQ3Gojbfu",
  checkMarkMargin: "0 12px 0 4px",
  spacing: "10px",
  statusBarPadding: "34px",
  tertiaryProductBadge: "hide",
  useCustomFonts: "false",
}

export const defaultJson = {
  pageSettings: defaultPageSettings,
  backgroundContent: [],
  mainContent: [],
  cardContent: [],
}

export const updateJsonByElement = (parsedJson: TPaywallTemplate, element: TElement) => {
  const { backgroundContent, mainContent, cardContent } = parsedJson

  const updateElement = (content: TElement[], element) => {
    return content.map((el) => {
      if (el.id === element.id) {
        return element
      } else if (el.children) {
        return {
          ...el,
          children: updateElement(el.children, element),
        }
      } else {
        return el
      }
    })
  }

  const updatedBackgroundContent = updateElement(backgroundContent || [], element)
  const updatedMainContent = updateElement(mainContent || [], element)
  const updatedCardContent = updateElement(cardContent || [], element)
  const updatedJson = {
    ...parsedJson,
    backgroundContent: updatedBackgroundContent,
    mainContent: updatedMainContent,
    cardContent: updatedCardContent,
  }

  return {
    json: JSON.stringify(updatedJson, null, 2),
    parsedJson: updatedJson,
  }
}

export const moveElement = (
  parsedJson: TPaywallTemplate,
  element: TElement,
  direction: "up" | "down"
) => {
  const { backgroundContent, mainContent, cardContent } = parsedJson

  const addIt = (content: TElement[], element, direction: "up" | "down") => {
    const index = content.findIndex((el) => el.id === element.id)

    if (index === -1) {
      return content.map((el) => {
        if (el.children) {
          return {
            ...el,
            children: addIt(el.children, element, direction),
          }
        } else {
          return el
        }
      })
    }

    if (
      (index === 0 && direction === "up") ||
      (index === content.length - 1 && direction === "down")
    ) {
      return content
    }

    const newIndex = direction === "up" ? index - 1 : index + 1
    const newContent = [...content]
    newContent.splice(index, 1)
    newContent.splice(newIndex, 0, element)
    return newContent
  }

  const updatedBackgroundContent = addIt(backgroundContent || [], element, direction)
  const updatedMainContent = addIt(mainContent || [], element, direction)
  const updatedCardContent = addIt(cardContent || [], element, direction)
  const updatedJson = {
    ...parsedJson,
    backgroundContent: updatedBackgroundContent,
    mainContent: updatedMainContent,
    cardContent: updatedCardContent,
  }

  return {
    json: JSON.stringify(updatedJson, null, 2),
    parsedJson: updatedJson,
  }
}

export const addSibling = ({
  parsedJson,
  elementId,
  newElement,
  placement,
}: {
  parsedJson: TPaywallTemplate
  elementId: string
  newElement: TElement
  placement: "before" | "after"
}) => {
  const { backgroundContent, mainContent, cardContent } = parsedJson

  const addIt = (
    content: TElement[],
    elementId: string,
    newElement: TElement,
    placement: "before" | "after"
  ) => {
    const index = content.findIndex((el) => el.id === elementId)

    if (index === -1) {
      return content.map((el) => {
        if (el.children) {
          return {
            ...el,
            children: addIt(el.children, elementId, newElement, placement),
          }
        } else {
          return el
        }
      })
    }

    let newContent: any = []

    content.forEach((el, i) => {
      if (i === index && placement === "before") {
        newContent.push(newElement)
      }
      newContent.push(el)
      if (i === index && placement === "after") {
        newContent.push(newElement)
      }
    })

    return newContent
  }

  const updatedBackgroundContent = addIt(backgroundContent || [], elementId, newElement, placement)
  const updatedMainContent = addIt(mainContent || [], elementId, newElement, placement)
  const updatedCardContent = addIt(cardContent || [], elementId, newElement, placement)
  const updatedJson = {
    ...parsedJson,
    backgroundContent: updatedBackgroundContent,
    mainContent: updatedMainContent,
    cardContent: updatedCardContent,
  }

  return {
    json: JSON.stringify(updatedJson, null, 2),
    parsedJson: updatedJson,
  }
}

export const addComponentToContent = ({
  parsedJson,
  content,
  newElement,
}: {
  parsedJson: TPaywallTemplate
  content: "backgroundContent" | "mainContent" | "cardContent"
  newElement: TElement
}) => {
  const { backgroundContent, mainContent, cardContent } = parsedJson

  const updatedJson = {
    ...parsedJson,
    backgroundContent:
      content === "backgroundContent"
        ? [...(backgroundContent || []), newElement]
        : backgroundContent,
    mainContent: content === "mainContent" ? [...(mainContent || []), newElement] : mainContent,
    cardContent: content === "cardContent" ? [...(cardContent || []), newElement] : cardContent,
  }

  return {
    json: JSON.stringify(updatedJson, null, 2),
    parsedJson: updatedJson,
  }
}

export const deleteElement = ({
  parsedJson,
  elementId,
}: {
  parsedJson: TPaywallTemplate
  elementId: string
}) => {
  if (!elementId) return

  const { backgroundContent, mainContent, cardContent } = parsedJson

  const deleteIt = (content: TElement[], elementId: string) => {
    const index = content.findIndex((el) => el.id === elementId)

    if (index === -1) {
      return content.map((el) => {
        if (el.children) {
          return {
            ...el,
            children: deleteIt(el.children, elementId),
          }
        } else {
          return el
        }
      })
    }

    const newContent = [...content]
    newContent.splice(index, 1)
    return newContent
  }

  const updatedBackgroundContent = deleteIt(backgroundContent || [], elementId)
  const updatedMainContent = deleteIt(mainContent || [], elementId)
  const updatedCardContent = deleteIt(cardContent || [], elementId)
  const updatedJson = {
    ...parsedJson,
    backgroundContent: updatedBackgroundContent,
    mainContent: updatedMainContent,
    cardContent: updatedCardContent,
  }

  return {
    json: JSON.stringify(updatedJson, null, 2),
    parsedJson: updatedJson,
  }
}

export const addChildToElement = ({
  parsedJson,
  elementId,
  newElement,
}: {
  parsedJson: TPaywallTemplate
  elementId: string
  newElement: TElement
}) => {
  const { backgroundContent, mainContent, cardContent } = parsedJson

  const addIt = (content: TElement[], elementId: string, newElement: TElement) => {
    const index = content.findIndex((el) => el.id === elementId)

    if (index === -1) {
      return content.map((el) => {
        if (el.children) {
          return {
            ...el,
            children: addIt(el.children, elementId, newElement),
          }
        } else {
          return el
        }
      })
    }

    return content.map((el) => {
      if (el.id === elementId) {
        if (elementsWithChildren.includes(el.name)) {
          return {
            ...el,
            children: [...(el.children || []), newElement],
          }
        }
        return el
      } else {
        return el
      }
    })
  }

  const updatedBackgroundContent = addIt(backgroundContent || [], elementId, newElement)
  const updatedMainContent = addIt(mainContent || [], elementId, newElement)
  const updatedCardContent = addIt(cardContent || [], elementId, newElement)
  const updatedJson = {
    ...parsedJson,
    backgroundContent: updatedBackgroundContent,
    mainContent: updatedMainContent,
    cardContent: updatedCardContent,
  }

  return {
    json: JSON.stringify(updatedJson, null, 2),
    parsedJson: updatedJson,
  }
}

export const duplicateElement = ({
  parsedJson,
  element,
}: {
  parsedJson: TPaywallTemplate
  element: TElement
}) => {
  const { id: originalId } = element || {}

  if (!originalId) return

  const updateAllElementIds = (element: TElement) => {
    const newId = createComponentId()
    let children

    if (element?.children !== undefined) {
      children = element.children.map((child) => {
        return updateAllElementIds(child)
      })
    }

    let newElement = {
      ...element,
      id: newId,
    }

    if (children) {
      newElement = {
        ...newElement,
        children,
      }
    }

    return newElement
  }

  const newElement = updateAllElementIds(element)

  const result = addSibling({
    parsedJson,
    elementId: originalId,
    newElement,
    placement: "after",
  })

  return { ...result, newElementId: newElement.id, newElement }
}

export const flattenArray = (array) => {
  let result: any[] = []

  for (let i = 0; i < array.length; i++) {
    let item = array[i]
    if (Array.isArray(item.children)) {
      result.push(item)
      result = result.concat(flattenArray(item.children))
    } else {
      result.push(item)
    }
  }

  return result
}

export const setDefaultsForEveryElement = (parsedJson: TPaywallTemplate) => {
  const { backgroundContent, mainContent, cardContent } = parsedJson

  const setDefaults = (el: TElement) => {
    let { children, container, childrenContainer } = el

    if (children) {
      children = children.map((child) => {
        return setDefaults(child)
      })
    }

    if (container) {
      container = { ...defaultContainerOptions, ...container }
    }

    if (childrenContainer) {
      childrenContainer = { ...defaultChildrenContainer, ...childrenContainer }
    }

    const newElement = { ...el, children, container, childrenContainer }
    return _.omitBy(newElement, _.isUndefined)
  }

  const updatedBackgroundContent = _.map(backgroundContent, (el) => {
    return setDefaults(el)
  })

  const updatedMainContent = _.map(mainContent, (el) => {
    return setDefaults(el)
  })

  const updatedCardContent = _.map(cardContent, (el) => {
    return setDefaults(el)
  })

  return {
    ...parsedJson,
    backgroundContent: updatedBackgroundContent,
    mainContent: updatedMainContent,
    cardContent: updatedCardContent,
  }
}

export const setProductCount = (
  parsedJson: TPaywallTemplate,
  productCount: "one" | "two" | "three"
) => {
  const { backgroundContent, mainContent, cardContent } = parsedJson

  const setCount = (el: TElement) => {
    if (el.name === "products") {
      return {
        ...el,
        settings: {
          ...el.settings,
          productCount,
        },
      }
    } else if (el.children) {
      return {
        ...el,
        children: el.children.map((child) => {
          return setCount(child)
        }),
      }
    }
    return el
  }

  const updatedBackgroundContent = (backgroundContent || []).map(setCount)
  const updatedMainContent = (mainContent || []).map(setCount)
  const updatedCardContent = (cardContent || []).map(setCount)

  return {
    ...parsedJson,
    backgroundContent: updatedBackgroundContent,
    mainContent: updatedMainContent,
    cardContent: updatedCardContent,
  }
}

export const createHeadingWithBadge = () => {
  const heading = { id: createComponentId(), ...defaultHeadingComponent }
  const badge = { id: createComponentId(), ...defaultBadgeComponent }

  return {
    id: createComponentId(),
    ...defaultDivComponent,
    settings: {
      ...defaultDivComponent.settings,
      initialStyles: {
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
        flexDirection: "row",
        gap: "8px",
        position: "relative",
      },
    },
    children: [heading, badge],
  }
}

export const createButtonWithLabel = ({
  flexDirection = "horizontal",
}: {
  flexDirection?: "horizontal" | "vertical"
} = {}) => {
  const heading = {
    id: createComponentId(),
    ...defaultHeadingComponent,
    settings: {
      ...defaultHeadingComponent.settings,
      type: "h3",
      initialStyles: { fontWeight: "500" },
    },
  }
  const button = { id: createComponentId(), ...defaultButtonBaseComponent }

  const flexCss =
    flexDirection === "horizontal"
      ? {
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }
      : {
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }

  return {
    id: createComponentId(),
    ...defaultDivComponent,
    settings: {
      ...defaultDivComponent.settings,
      initialStyles: {
        display: "flex",
        ...flexCss,
        gap: "8px",
        position: "relative",
      },
    },
    children: [heading, button],
  }
}

export const createContentBlock = (
  { alignment = "left" }: { alignment?: "left" | "center" } = { alignment: "left" }
) => {
  const titleMain = {
    id: createComponentId(),
    ...defaultTextComponent,
    settings: {
      ...defaultTextComponent.settings,
      initialStyles: {
        fontSize: "20px",
        fontWeight: "600",
      },
      text: "Content Block Title",
    },
  }

  const titleRight = {
    id: createComponentId(),
    ...defaultTextComponent,
    settings: {
      ...defaultTextComponent.settings,
      initialStyles: {
        fontSize: "20px",
        fontWeight: "600",
      },
      text: "Right",
    },
  }

  const caption = {
    id: createComponentId(),
    ...defaultTextComponent,
    settings: {
      ...defaultTextComponent.settings,
      initialStyles: {
        marginBottom: "8px",
        opacity: "0.5",
        fontSize: "14px",
        fontWeight: "inherit",
        textAlign: alignment === "left" ? "left" : "center",
        whiteSpace: "nowrap",
      },
      text: "Caption",
    },
  }

  const paragraph = {
    id: createComponentId(),
    ...defaultTextComponent,
    settings: {
      ...defaultTextComponent.settings,
      isParagraph: true,
      initialStyles: {
        fontSize: "17px",
        textAlign: alignment === "left" ? "left" : "center",
      },
    },
  }

  const flexCss =
    alignment === "left"
      ? {
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }
      : {
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }

  return {
    id: createComponentId(),
    ...defaultDivComponent,
    children: [
      {
        id: createComponentId(),
        ...defaultDivComponent,
        settings: {
          ...defaultDivComponent.settings,
          initialStyles: {
            display: "flex",
            ...flexCss,
            position: "relative",
          },
        },
        children: [titleMain, titleRight],
      },
      caption,
      paragraph,
    ],
  }
}

export const createTimeline = () => {
  return {
    id: createComponentId(),
    ...defaultTimelineComponent,
    children: [
      { id: createComponentId(), ...defaultTimelineItemComponent },
      { id: createComponentId(), ...defaultTimelineItemComponent },
      { id: createComponentId(), ...defaultTimelineItemComponent },
    ],
  }
}

export const createParagraph = () => {
  return {
    id: createComponentId(),
    ...defaultTextComponent,
    settings: {
      ...defaultTextComponent.settings,
      isParagraph: true,
    },
  }
}

export const createDefaultFooter = (ids: string[] = []) => {
  return {
    id: ids[0] || createComponentId(),
    ...defaultDivComponent,
    customName: "Footer",
    settings: {
      ...defaultDivComponent.settings,
      initialStyles: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-around",
        alignItems: "center",
        paddingTop: "calc(var(--sw-container-padding) * 0.6)",
        paddingBottom: "calc(var(--sw-container-padding) * 0.6)",
      },
    },
    children: [
      {
        id: ids[1] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "Terms of Use",
        },
      },
      {
        id: ids[2] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "Restore Purchase",
        },
      },
      {
        id: ids[3] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "Privacy Policy",
        },
      },
    ],
  }
}

export const createCompactFooter = (ids: string[] = []) => {
  return {
    id: ids[0] || createComponentId(),
    ...defaultDivComponent,
    customName: "Footer",
    settings: {
      ...defaultDivComponent.settings,
      initialStyles: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: "calc(var(--sw-container-padding) * 0.6)",
        paddingBottom: "calc(var(--sw-container-padding) * 0.6)",
      },
    },
    children: [
      {
        id: ids[1] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            padding: "0px calc(var(--sw-font-size) * 0.125)",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "Terms",
        },
      },
      {
        id: ids[2] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            padding: "0px calc(var(--sw-font-size) * 0.125)",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "&",
        },
      },
      {
        id: ids[3] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            padding: "0px calc(var(--sw-font-size) * 0.125)",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "Privacy",
        },
      },
      {
        id: ids[4] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            padding: "0px calc(var(--sw-font-size) * 0.125)",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "|",
        },
      },
      {
        id: ids[5] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            padding: "0px calc(var(--sw-font-size) * 0.125)",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "Renews for $0.00 per day",
        },
      },
    ],
  }
}

export const createMinimalFooter = (ids: string[] = []) => {
  return {
    id: ids[0] || createComponentId(),
    ...defaultDivComponent,
    customName: "Footer",
    settings: {
      ...defaultDivComponent.settings,
      initialStyles: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-around",
        alignItems: "center",
        paddingTop: "calc(var(--sw-container-padding) * 0.6)",
        paddingBottom: "calc(var(--sw-container-padding) * 0.6)",
      },
    },
    children: [
      {
        id: ids[1] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "Terms of Use",
        },
      },
      {
        id: ids[2] || createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          initialStyles: {
            display: "inline-block",
            fontSize: "calc(var(--sw-font-size) * 0.75);",
          },
          text: "Privacy Policy",
        },
      },
    ],
  }
}

export const createModal = (triggerElement) => {
  if (!triggerElement) return

  return {
    id: createComponentId(),
    name: "modal",
    children: [
      triggerElement,
      {
        id: createComponentId(),
        ...defaultCloseModalComponent,
        settings: {
          initialStyles: {
            position: "absolute",
            top: "0px",
            right: "0px",
          },
        },
        children: [
          {
            id: createComponentId(),
            ...defaultImageBaseComponent,
            settings: {
              initialStyles: {
                width: "24px",
                height: "24px",
              },
              src: "../public/assets/nav-button.svg",
              altText: "Close Modal Button",
            },
          },
        ],
      },
      {
        id: createComponentId(),
        ...defaultHeadingComponent,
        settings: {
          ...defaultHeadingComponent.settings,
          initialStyles: {
            marginBottom: "20px",
          },
        },
      },
      {
        id: createComponentId(),
        ...defaultTextComponent,
        settings: {
          ...defaultTextComponent.settings,
          isParagraph: true,
        },
      },
      {
        id: createComponentId(),
        ...defaultCloseModalComponent,
        settings: {
          initialStyles: {
            marginTop: "20px",
          },
        },
        children: [
          {
            id: createComponentId(),
            ...defaultButtonBaseComponent,
            settings: {
              text: "Close Modal",
            },
          },
        ],
      },
    ],
  }
}

export const createMarqueeItems = (marqueeItemCount: TMarqueeItemCount) => {
  const marqueeItems: any[] = (
    Array.from(Array(parseInt(marqueeItemCount)), (x, i) => i) || []
  ).map((i) => {
    const newId = createComponentId()
    return { id: newId, ...defaultComponentMap.marqueeItem }
  })

  return marqueeItems
}

export const createMaquee = (marqueeItemCount?: TMarqueeItemCount) => {
  const marqueeItems = createMarqueeItems(marqueeItemCount || "6")

  return {
    id: createComponentId(),
    ...defaultMarqueeComponent,
    children: marqueeItems,
  }
}

export const defaultComponentMap = {
  badge: defaultBadgeComponent,
  bullet: defaultBulletComponent,
  button: defaultButtonComponent,
  buttonBase: defaultButtonBaseComponent,
  card: defaultCardComponent,
  closeModal: defaultCloseModalComponent,
  container: defaultContainerComponent,
  div: defaultDivComponent,
  divider: defaultDividerComponent,
  dividingHeader: defaultDividingHeaderComponent,
  drawer: defaultDrawerComponent,
  dropdown: defaultDropdownComponent,
  heading: defaultHeadingComponent,
  html: defaultHTMLComponent,
  image: defaultImageComponent,
  imageBase: defaultImageBaseComponent,
  list: defaultListComponent,
  listV2: defaultListV2Component,
  lottie: defaultLottieComponent,
  marquee: defaultMarqueeComponent,
  marqueeItem: defaultMarqueeItemComponent,
  modal: defaultModalComponent,
  paragraph: defaultParagraphComponent,
  products: defaultProductsComponent,
  rating: defaultRatingComponent,
  review: defaultReviewComponent,
  section: defaultSectionComponent,
  subheading: defaultSubheadingComponent,
  subtitle: defaultSubtitleComponent,
  table: defaultTableComponent,
  text: defaultTextComponent,
  timeline: defaultTimelineComponent,
  timelineItem: defaultTimelineItemComponent,
  title: defaultTitleComponent,
  video: defaultVideoComponent,
}

type options = {
  defaultId?: string
  modalTriggerElement?: any
  marqueeItemCount?: TMarqueeItemCount
}

export const createComponent = (
  componentName: string,
  options: options = {},
  pageSettings: TPageSettings = {}
) => {
  if (componentName === "headingWithBadge") {
    return createHeadingWithBadge()
  } else if (componentName === "buttonWithLabelHorizontal") {
    return createButtonWithLabel()
  } else if (componentName === "buttonWithLabelVertical") {
    return createButtonWithLabel({ flexDirection: "vertical" })
  } else if (componentName === "contentBlock") {
    return createContentBlock()
  } else if (componentName === "contentBlockCentered") {
    return createContentBlock({ alignment: "center" })
  } else if (componentName === "timeline") {
    return createTimeline()
  } else if (componentName === "paragraph") {
    return createParagraph()
  } else if (componentName === "modal") {
    const triggerComponent = createComponent(options.modalTriggerElement)
    return createModal(triggerComponent)
  } else if (componentName === "marquee") {
    return createMaquee(options.marqueeItemCount)
  } else if (componentName === "footer") {
    return createDefaultFooter()
  } else if (componentName === "footerCompact") {
    return createCompactFooter()
  } else if (componentName === "footerMinimal") {
    return createMinimalFooter()
  } else if (componentName === "products") {
    const products = { ...defaultComponentMap[componentName] }
    return {
      id: createComponentId(),
      ...products,
      settings: {
        ...products.settings,
        productCount: pageSettings?.productCount || "one",
      },
    }
  } else {
    const id =
      typeof options?.defaultId === "string" && options?.defaultId.length > 0
        ? options?.defaultId
        : createComponentId()
    return { id, ...defaultComponentMap[componentName] }
  }
}

/**
 * Updates the product count for all product components
 * as well as the page settings object.
 */
export const updateAllProductsCount = ({
  parsedJson,
  productCount,
}: {
  parsedJson: TPaywallTemplate
  productCount: "one" | "two" | "three"
}) => {
  const { pageSettings, mainContent } = parsedJson

  const updateCount = (content: any[]) => {
    return content.map((item) => {
      if (item.name === "products") {
        return {
          ...item,
          settings: {
            ...item.settings,
            productCount,
          },
        }
      } else if (item.children) {
        return {
          ...item,
          children: updateCount(item.children),
        }
      } else {
        return item
      }
    })
  }

  const updatedMainContent = updateCount(mainContent || [])

  const updatedJson = {
    ...parsedJson,
    pageSettings: {
      ...pageSettings,
      productCount,
    },
    mainContent: updatedMainContent,
  }

  return {
    json: JSON.stringify(updatedJson, null, 2),
    parsedJson: updatedJson,
  }
}
